import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus, Wallet, ArrowDownRight, ArrowUpRight, AlertTriangle, Clock3,
    Loader2, Receipt, TrendingUp, CheckCircle2, XCircle, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useAuth } from "@/context/AuthContext";
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import Forbidden from "@/pages/Forbidden";

const FINANCE_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant"]);

const STATUS_TR = {
    unpaid: "Ödenmedi", partial: "Kısmi", paid: "Ödendi",
    overdue: "Gecikmiş", cancelled: "İptal",
};
const STATUS_STYLE = {
    unpaid: "bg-muted text-muted-foreground",
    partial: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    paid: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    overdue: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    cancelled: "bg-muted text-muted-foreground line-through",
};

const EXP_STATUS_TR = { pending: "Bekliyor", approved: "Onaylandı", rejected: "Reddedildi", reimbursed: "Ödendi" };
const EXP_STATUS_STYLE = {
    pending: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    approved: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    rejected: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    reimbursed: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
};

const inputCls = "h-10 w-full rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40";

const fmtMoney = (v, c = "TRY") => {
    try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: c || "TRY", maximumFractionDigits: 0 }).format(v || 0); }
    catch { return `${v} ${c}`; }
};
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("tr-TR") : "—"; } catch { return iso; } };

function StatCard({ Icon, label, value, sub, accent = "bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]" }) {
    return (
        <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${accent}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[11.5px] text-muted-foreground uppercase tracking-wide">{label}</div>
                    <div className="text-[18px] font-bold tabular-nums truncate">{value}</div>
                    {sub && <div className="text-[10.5px] text-muted-foreground truncate">{sub}</div>}
                </div>
            </div>
        </div>
    );
}

// ------------------- Overview Tab -------------------
function OverviewTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try { const r = await api.get("/finance/dashboard"); setData(r.data); }
            catch (e) { toast.error(e?.response?.data?.detail || "Yüklenemedi."); }
            finally { setLoading(false); }
        })();
    }, []);
    if (loading) return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor…</div>;
    if (!data) return null;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard Icon={ArrowDownRight} label="Toplam Alacak" value={fmtMoney(data.receivables?.outstanding)} sub={`${data.receivables?.count || 0} fatura • ${fmtMoney(data.receivables?.paid)} tahsil edildi`} accent="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" />
                <StatCard Icon={ArrowUpRight} label="Toplam Borç" value={fmtMoney(data.payables?.outstanding)} sub={`${data.payables?.count || 0} fatura`} accent="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]" />
                <StatCard Icon={AlertTriangle} label="Gecikmiş Fatura" value={data.overdue_count || 0} sub="Vadesi geçmiş kayıt" accent="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]" />
                <StatCard Icon={Clock3} label="7 Gün İçinde Vadesi" value={data.upcoming_count || 0} sub="Yaklaşan ödemeler" accent="bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card-elevated p-5">
                    <div className="text-[13px] font-semibold mb-3">Bekleyen Fiş Onayı</div>
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[26px] font-bold tabular-nums">{data.pending_expenses?.count || 0}</div>
                            <div className="text-[11.5px] text-muted-foreground">Personel gider talebi</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[16px] font-bold text-[hsl(var(--warning))]">{fmtMoney(data.pending_expenses?.amount)}</div>
                            <div className="text-[11px] text-muted-foreground">Toplam tutar</div>
                        </div>
                    </div>
                </div>
                <div className="card-elevated p-5">
                    <div className="text-[13px] font-semibold mb-3">Net Durum</div>
                    <div className="text-[26px] font-bold tabular-nums">
                        {fmtMoney((data.receivables?.outstanding || 0) - (data.payables?.outstanding || 0))}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground">Alacak − Borç</div>
                </div>
            </div>
        </div>
    );
}

// ------------------- Invoice Modal -------------------
function InvoiceModal({ open, onClose, onSaved, defaultType = "receivable", customers }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        type: defaultType, customer_id: "", counterparty_name: "",
        amount: "", currency: "TRY", invoice_number: "",
        description: "", issued_date: new Date().toISOString().slice(0, 10),
        due_date: "",
    });
    useEffect(() => {
        if (open) setForm({
            type: defaultType, customer_id: "", counterparty_name: "",
            amount: "", currency: "TRY", invoice_number: "",
            description: "", issued_date: new Date().toISOString().slice(0, 10),
            due_date: "",
        });
    }, [open, defaultType]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.amount || Number(form.amount) <= 0) return toast.error("Tutar zorunludur.");
        if (form.type === "receivable" && !form.customer_id) return toast.error("Alacak için müşteri seçin.");
        setSaving(true);
        try {
            const payload = { ...form, amount: Number(form.amount) };
            if (!payload.due_date) delete payload.due_date;
            if (!payload.customer_id) delete payload.customer_id;
            if (!payload.counterparty_name) delete payload.counterparty_name;
            const { data } = await api.post("/finance/invoices", payload);
            toast.success("Fatura eklendi.");
            onSaved && onSaved(data);
            onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl">
                <DialogHeader><DialogTitle>{form.type === "receivable" ? "Yeni Alacak (Fatura)" : "Yeni Borç"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <div className="grid grid-cols-2 gap-2">
                        <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="invoice-type-select">
                            <option value="receivable">Alacak</option>
                            <option value="payable">Borç</option>
                        </select>
                        <input placeholder="Fatura No" className={inputCls} value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
                    </div>
                    {form.type === "receivable" ? (
                        <select required className={inputCls} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="invoice-customer-select">
                            <option value="">Müşteri seçin…</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    ) : (
                        <input placeholder="Karşı taraf / tedarikçi" className={inputCls} value={form.counterparty_name} onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })} />
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        <input required type="number" step="0.01" placeholder="Tutar" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount-input" />
                        <select className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                            <option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[11px] text-muted-foreground mb-1">Fatura Tarihi</label>
                            <input type="date" className={inputCls} value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-[11px] text-muted-foreground mb-1">Vade</label>
                            <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-input" />
                        </div>
                    </div>
                    <textarea rows={2} placeholder="Açıklama" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="invoice-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Kaydet</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ------------------- Payment Modal -------------------
function PaymentModal({ invoice, onClose, onDone }) {
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    if (!invoice) return null;
    const outstanding = (invoice.amount || 0) - (invoice.paid_amount || 0);
    const submit = async (e) => {
        e.preventDefault();
        const n = Number(amount);
        if (!n || n <= 0) return toast.error("Geçerli bir tutar girin.");
        if (n > outstanding + 0.001) return toast.error(`Kalan bakiye ${fmtMoney(outstanding, invoice.currency)}`);
        setSaving(true);
        try {
            const { data } = await api.patch(`/finance/invoices/${invoice.id}/payment`, { amount: n, note });
            toast.success("Ödeme kaydedildi.");
            onDone && onDone(data);
        } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi."); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader><DialogTitle>Ödeme Kaydet</DialogTitle></DialogHeader>
                <div className="text-[12.5px] text-muted-foreground">
                    <b>{invoice.customer_name || invoice.counterparty_name || "—"}</b> • {invoice.invoice_number || "Fatura"} • Kalan: <b className="text-foreground tabular-nums">{fmtMoney(outstanding, invoice.currency)}</b>
                </div>
                <form onSubmit={submit} className="space-y-3 pt-2">
                    <input required type="number" step="0.01" placeholder="Ödeme tutarı" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-amount-input" />
                    <input placeholder="Not (opsiyonel)" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
                    <DialogFooter>
                        <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-semibold hover:bg-muted">{tr.common.cancel}</button>
                        <button type="submit" disabled={saving} className="btn-brand" data-testid="payment-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Ödemeyi Kaydet</button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ------------------- Invoices Table -------------------
function InvoicesTable({ type, customers }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [openNew, setOpenNew] = useState(false);
    const [payFor, setPayFor] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page_size: 200 };
            if (status) params.status = status;
            if (customerId) params.customer_id = customerId;
            const { data } = await api.get(`/finance/${type === "receivable" ? "receivables" : "payables"}`, { params });
            setRows(data.items || []);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [type, status, customerId]);
    useEffect(() => { load(); }, [load]);

    const del = async (id) => {
        if (!window.confirm("Bu fatura silinsin mi?")) return;
        try { await api.delete(`/finance/invoices/${id}`); toast.success("Silindi."); setRows((r) => r.filter((x) => x.id !== id)); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    return (
        <div className="space-y-4">
            <div className="card-elevated p-4 flex flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                    <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} max-w-[180px]`}>
                        <option value="">Tüm Durumlar</option>
                        {Object.entries(STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {type === "receivable" && (
                        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={`${inputCls} max-w-[220px]`}>
                            <option value="">Tüm Müşteriler</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                </div>
                <button onClick={() => setOpenNew(true)} className="btn-brand h-10" data-testid={`btn-new-${type}`}>
                    <Plus className="h-4 w-4" /> {type === "receivable" ? "Yeni Alacak" : "Yeni Borç"}
                </button>
            </div>

            <div className="card-elevated overflow-hidden">
                <table className="w-full min-w-[900px]">
                    <thead><tr className="border-b border-border bg-muted/40">
                        {[type === "receivable" ? "Müşteri" : "Karşı Taraf", "Fatura No", "Tutar", "Ödenen", "Kalan", "Vade", "Durum", "İşlem"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[11.5px] font-semibold uppercase text-muted-foreground">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan="8" className="px-4 py-12 text-center text-muted-foreground">Yükleniyor…</td></tr>
                        : rows.length === 0 ? <tr><td colSpan="8" className="px-4 py-12 text-center text-muted-foreground">Kayıt yok.</td></tr>
                        : rows.map((r) => {
                            const outstanding = (r.amount || 0) - (r.paid_amount || 0);
                            return (
                                <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                                    <td className="px-4 py-3 text-[13px] font-semibold">{r.customer_name || r.counterparty_name || "—"}</td>
                                    <td className="px-4 py-3 text-[12.5px] text-muted-foreground">{r.invoice_number || "—"}</td>
                                    <td className="px-4 py-3 text-[13px] font-bold tabular-nums">{fmtMoney(r.amount, r.currency)}</td>
                                    <td className="px-4 py-3 text-[12.5px] tabular-nums text-[hsl(var(--success))]">{fmtMoney(r.paid_amount, r.currency)}</td>
                                    <td className="px-4 py-3 text-[12.5px] tabular-nums">{fmtMoney(outstanding, r.currency)}</td>
                                    <td className="px-4 py-3 text-[12.5px] text-muted-foreground">{fmtDate(r.due_date)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[r.status]}`}>{STATUS_TR[r.status]}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1">
                                            {r.status !== "paid" && r.status !== "cancelled" && (
                                                <button onClick={() => setPayFor(r)} className="text-[11.5px] font-semibold rounded-lg bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-2 py-1 hover:bg-[hsl(var(--brand))]/20" data-testid={`btn-payment-${r.id}`}>Ödeme</button>
                                            )}
                                            <button onClick={() => del(r.id)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-[hsl(var(--destructive))]"><Trash2 className="h-3.5 w-3.5" /></button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <InvoiceModal open={openNew} onClose={() => setOpenNew(false)} onSaved={(x) => setRows((r) => [x, ...r])} defaultType={type} customers={customers} />
            <PaymentModal invoice={payFor} onClose={() => setPayFor(null)} onDone={(x) => { setPayFor(null); setRows((r) => r.map((i) => (i.id === x.id ? x : i))); }} />
        </div>
    );
}

// ------------------- Expense Approvals -------------------
function ExpenseApprovals() {
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("pending");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/finance/expense-approvals", { params: { status, page_size: 200 } });
            setRows(data.items || []);
        } catch (err) { toast.error(err?.response?.data?.detail || "Yüklenemedi."); }
        finally { setLoading(false); }
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const updateStatus = async (id, newStatus, reason) => {
        try {
            const body = { status: newStatus };
            if (reason) body.reject_reason = reason;
            await api.post(`/expenses/${id}/status`, body);
            toast.success("Durum güncellendi.");
            setRows((r) => r.filter((x) => x.id !== id));
        } catch (err) { toast.error(err?.response?.data?.detail || "Güncellenemedi."); }
    };

    return (
        <div className="space-y-4">
            <div className="card-elevated p-4 flex gap-3 items-center">
                <div className="text-[12.5px] text-muted-foreground">Durum</div>
                <select className={`${inputCls} max-w-[180px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {Object.entries(EXP_STATUS_TR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>
            <div className="card-elevated overflow-hidden">
                <table className="w-full min-w-[900px]">
                    <thead><tr className="border-b border-border bg-muted/40">
                        {["Personel", "Başlık", "Kategori", "Tutar", "Tarih", "Durum", "İşlem"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-[11.5px] font-semibold uppercase text-muted-foreground">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">Yükleniyor…</td></tr>
                        : rows.length === 0 ? <tr><td colSpan="7" className="px-4 py-12 text-center text-muted-foreground">Bu durumda kayıt yok.</td></tr>
                        : rows.map((r) => (
                            <tr key={r.id} className="border-b border-border last:border-b-0 hover:bg-muted/40">
                                <td className="px-4 py-3 text-[13px] font-semibold">{r.user_name || "—"}<div className="text-[11px] text-muted-foreground font-normal">{r.user_department || "—"}</div></td>
                                <td className="px-4 py-3 text-[13px]">{r.title}{r.description && <div className="text-[11px] text-muted-foreground truncate max-w-[240px]">{r.description}</div>}</td>
                                <td className="px-4 py-3 text-[12px]"><span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold">{r.category}</span></td>
                                <td className="px-4 py-3 text-[13px] font-bold tabular-nums">{fmtMoney(r.amount, r.currency)}</td>
                                <td className="px-4 py-3 text-[12.5px] text-muted-foreground">{fmtDate(r.expense_date)}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${EXP_STATUS_STYLE[r.status]}`}>{EXP_STATUS_TR[r.status]}</span></td>
                                <td className="px-4 py-3">
                                    {r.status === "pending" && (
                                        <div className="flex gap-1">
                                            <button onClick={() => updateStatus(r.id, "approved")} className="text-[11.5px] font-semibold rounded-lg bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] px-2 py-1 hover:bg-[hsl(var(--success))]/20" data-testid={`btn-approve-${r.id}`}>Onayla</button>
                                            <button onClick={() => {
                                                const reason = window.prompt("Red gerekçesi (opsiyonel):");
                                                updateStatus(r.id, "rejected", reason || undefined);
                                            }} className="text-[11.5px] font-semibold rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] px-2 py-1 hover:bg-[hsl(var(--destructive))]/20" data-testid={`btn-reject-${r.id}`}>Reddet</button>
                                        </div>
                                    )}
                                    {r.status === "approved" && (
                                        <button onClick={() => updateStatus(r.id, "reimbursed")} className="text-[11.5px] font-semibold rounded-lg bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] px-2 py-1 hover:bg-[hsl(var(--info))]/20">Ödendi olarak işaretle</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ------------------- Main Page -------------------
export default function FinancePage() {
    const { user } = useAuth();
    const [customers, setCustomers] = useState([]);

    const canAccess = useMemo(() => user && FINANCE_ROLES.has(user.role), [user]);

    useEffect(() => {
        if (!canAccess) return;
        (async () => {
            try {
                const { data } = await api.get("/customers", { params: { page_size: 200 } });
                setCustomers((data.items || []).map((c) => ({ id: c.id, name: c.name })));
            } catch { /* silent */ }
        })();
    }, [canAccess]);

    if (!canAccess) return <Forbidden />;

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight" data-testid="finance-title">Maliye</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">Müşteri borç/alacak, yaklaşan ödemeler ve fiş onayları.</p>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
                    <Wallet className="h-3.5 w-3.5" /> Sadece Yönetici
                </div>
            </div>

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="bg-muted/50 p-1 rounded-xl">
                    <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="finance-tab-overview">Genel Bakış</TabsTrigger>
                    <TabsTrigger value="receivables" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="finance-tab-receivables">Alacaklar</TabsTrigger>
                    <TabsTrigger value="payables" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="finance-tab-payables">Borçlar</TabsTrigger>
                    <TabsTrigger value="approvals" className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-card" data-testid="finance-tab-approvals">Fiş Onayları</TabsTrigger>
                </TabsList>
                <TabsContent value="overview"><OverviewTab /></TabsContent>
                <TabsContent value="receivables"><InvoicesTable type="receivable" customers={customers} /></TabsContent>
                <TabsContent value="payables"><InvoicesTable type="payable" customers={customers} /></TabsContent>
                <TabsContent value="approvals"><ExpenseApprovals /></TabsContent>
            </Tabs>
        </div>
    );
}
